<template>
  <div class="printable-article">
    <div class="printable-article__body">
      <h3>{{ $t('configurator.headings.printable_article') }}</h3>
    </div>
    <div class="printable-article__parent">
      <div>
        <img v-if="hasImage" :src="previewImage" alt="" class="printable-article__image">
      </div>
      <div class="printable-article__body printable-article__body--description">
        <span class="printable-article__title">{{ name }}</span>
        <article>{{ description }}</article>

        <div class="printable-article__body-footer">
          <div>
            {{ $filters.formatMoney(price) }}
            <small class="article-unit" :class="article.unit">
              <span v-if="unitIsSqm" class="square_m">/m<sup>2</sup></span>
              <span v-if="unitIsPc" class="per_unit">{{ $t("booth_articles.per_unit") }}</span>
            </small>
          </div>
          <div v-if="article.pivot.bookable && daysToGo > article.pivot.bookable_till_in_days"
            class="input-group input-group-sm print__input-group">
            <span class="input-group-btn">
              <button
                class="btn btn-primary btn-sm"
                tabindex="-1"
                type="button"
                :disabled="article.count <= article.inclusive"
                @click="decreaseCount">
                <i class="fa fa-minus" aria-hidden="true"></i>
              </button>
            </span>
            <input
              class="form-control"
              type="number"
              :step="1"
              :min="article.inclusive"
              name="depth"
              v-model="article.count"
              @blur="updateBasket(article)">
            <span class="input-group-btn">
              <button
                class="btn btn-primary btn-sm"
                tabindex="-1"
                type="button"
                @click="increaseCount">
                <i class="fa fa-plus" aria-hidden="true"></i>
              </button>
            </span>
            <span class="input-group-btn" style="font-size:14px;">
              <small class="article-unit" v-if="unitIsSqm">m<sup>2</sup></small>
              <small class="article-unit" v-if="unitIsPc">&nbsp;&nbsp;</small>
            </span>
          </div>
          <div v-else class="text-right">
            {{ $t('article_not_available') }}
          </div>
        </div>
      </div>
    </div>
    <div class="printable-article__body">
      <hr>
      <h4 class="border--b-0">{{ $t('configurator.headings.print_configuration') }}</h4>
      <table class="table">
        <thead>
          <th>{{ $t('configurator.tables.columns.surface') }}</th>
          <th>{{ $t('configurator.tables.columns.print_article') }}</th>
          <th class="print-article__table__row-price">{{ $t('configurator.tables.columns.price_per_piece') }}</th>
          <th>{{ $t('configurator.tables.columns.count') }}</th>
          <th class="text-right print-article__table__row-price">{{ $t('configurator.tables.columns.price') }}</th>
        </thead>
        <tbody>
          
          <!-- Print Article Surface Count Selection -->
          <printable-item-surfaces
            :article="article"
            @show="showArticleModal"
          />
        </tbody>
      </table>
    </div>
  </div>
  <article-modal v-if="articleModalActive"
    :article="selectedArticle"
    @close="hideArticleModal">
  </article-modal>
</template>

<script setup>
import {computed, reactive, ref, watch} from 'vue'
import { useMainStore } from '@/store'
import { ARTICLE_DEFAULT_IMAGE, ARTICLE_TYPE_PRINTABLE, UNIT_PC, UNIT_SQM } from '@/constants'
import _ from 'lodash'
import { hideLoadingIndicator, showLoadingIndicator } from '@/utils/toast'
import Form from '@/classes/Form'
import { addLateDeliverySurcharge } from '@/utils/helper'

const store = useMainStore()
// define props
const props = defineProps({
  article: {
    type: Object,
    required: true
  }
})
// define emits
const emit = defineEmits(['saveBasket'])
// define reactive props
const articleModalActive = ref(false)
const addPrintModalActive = ref(false)

const selectedArticle = ref(null)
const dropzoneFile = ref('')
const printForm = reactive(new Form({
  print: null,
  file: null,
  slot: null
}))
// define watchers
watch(() => props.article.count, () => {
  // also reduce print article count if its too high
  printArticles.value.map(printArticle => {
    if (printArticle.count > props.article.count) {
      printArticle.count = props.article.count
    }
  })
})

// define computed props
const locale = computed(() => store.settings.locale)
const daysToGo = computed(() => store.tmp.days_to_go)
const basket = computed(() => store.settings.basket)
const shoppingCart = computed(() => store.settings.shopping_cart)
const earlyBirdMode = computed(() => store.tmp.early_bird_mode)
const lateDelivery = computed(() => store.tmp.late_delivery)

const inEnglish = computed(() => {
  return locale.value === 'en'
})

const hasImage = computed(() => {
  return props.article.images !== null && props.article.images.length > 0
})

const previewImage = computed(() => {
  if (!hasImage.value) {
    return ARTICLE_DEFAULT_IMAGE
  }

  return '/images/articles/' + props.article.images[0].path;
})

const name = computed(() => {
  return (inEnglish.value) ? props.article.name_en : props.article.name
})

const description = computed(() => {
  return (inEnglish.value) ? props.article.description_en : props.article.description
})

const unitIsPc = computed(() => {
  return props.article.unit === UNIT_PC
})

const unitIsSqm = computed(() => {
  return props.article.unit === UNIT_SQM
})

const printArticles = computed(() => {
  return shoppingCart.value.print_items.filter((item) => {
    return item.parent_id === props.article.id
  })
})

const price = computed(() => {
  return (earlyBirdMode.value)
    ? addLateDeliverySurcharge(props.article.exhibitionPrices.price_net, lateDelivery.value)
    : props.article.exhibitionPrices.price_net
})

// define methods
const increaseCount = () => {
  if (props.maxCount === props.article.count) {
    return
  }
  props.article.count++
	const clonedArticle =  _.cloneDeep(props.article)
	shoppingCart.value.addArticle(clonedArticle)
	if (clonedArticle.type === ARTICLE_TYPE_PRINTABLE) {
		addPrintArticle(clonedArticle)
  }
  emit('saveBasket')
}

const decreaseCount = () => {
  if (props.article.count === 0) {
    return
  }
  props.article.count--
	const clonedArticle =  _.cloneDeep(props.article)
	shoppingCart.value.removeArticle(clonedArticle)
	if (clonedArticle.type === ARTICLE_TYPE_PRINTABLE) {
		removePrintArticle(clonedArticle)
	}

  emit('saveBasket')
}

const addPrintArticle = (article) => {
	for (const printArticle of article.prints ?? []) {
		const index = basket.value.selectedArticles.findIndex((item) => item.id === printArticle.print_article_id)
		if (index > -1) {
			const clonedPrintArticle = _.cloneDeep(basket.value.selectedArticles[index])
			shoppingCart.value.addPrintArticle(clonedPrintArticle, printArticle)
		} else {
			console.warn('no print article found')
		}
	}
}

const removePrintArticle = (article) => {
	for (const printArticle of article.prints ?? []) {
		shoppingCart.value.removePrintArticle(article, printArticle.id)
	}

  shoppingCart.value.removePrintConfigurationGroup(article)
}

const getPrintArticle = (printArticle) => {
  return shoppingCart.value.print_items.find((item) => {
    return item.id === printArticle.id
  })
}

const checkIfIsBookable = (printArticle) => {
  const item = getPrintArticle(printArticle)
  return item.pivot.bookable && daysToGo.value > item.pivot.bookable_till_in_days
}

const getPrintArticleName = (printArticle) => {
  if (!props.article.hasOwnProperty('prints')) {
    return ''
  }
  
  const item = props.article.prints.find((item) => {
    return item.print_article_id === printArticle.id
  })
  if (!item) {
    return ''
  }

  return (inEnglish.value) ? item.name_en : item.name
}

const getPrintArticleId = (printArticle) => {
  if (!props.article.hasOwnProperty('prints')) {
    return ''
  }
  
  const item = props.article.prints.find((item) => {
    return item.print_article_id === printArticle.id
  })
  if (!item) {
    return ''
  }

  return item.id
}

const showArticleModal = (article) => {
  selectedArticle.value = article
  articleModalActive.value = true
}

const hideArticleModal = () => {
  articleModalActive.value = false
  selectedArticle.value = null
}

const updateBasket = (article) => {
	const clonedArticle =  _.cloneDeep(article)
	shoppingCart.value.updateArticleAmount(clonedArticle)
	if (article.count > 0) {
		if (clonedArticle.type === ARTICLE_TYPE_PRINTABLE) {
			addPrintArticle(clonedArticle)
		}
	} else {
		if (clonedArticle.type === ARTICLE_TYPE_PRINTABLE) {
			removePrintArticle(clonedArticle)
		}
	}

  emit('saveBasket')
}
</script>
